import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$isTableCellNode, TableCellNode} from '@lexical/table';
import {$createParagraphNode, LexicalNode, ParagraphNode} from 'lexical';
import {useEffect} from 'react';

import {EnhancedTableCellNode} from './EnhancedTableCellNode';
import {$isTableCellDecoratorNode} from './TableCellDecoratorNode';

/**
 * A table cell is a block container: Lexical and @lexical/table assume every
 * direct child of a cell is a block-level element node (normally a paragraph).
 * Malformed documents (typically from HTML pastes/imports that didn't wrap
 * inline content) can leave a bare TextNode/LineBreak directly inside a cell.
 * Clicking into such a cell makes the table/selection handlers traverse a
 * TextNode where they expect an ElementNode, throwing and crashing the editor.
 *
 * This transform heals those cells by wrapping any run of bare inline children
 * in a ParagraphNode. Decorator nodes (which are intentionally inline children
 * of a cell) and existing block children are left untouched.
 */
const isWrappableInline = (node: LexicalNode): boolean =>
  node.isInline() && !$isTableCellDecoratorNode(node);

const normalizeTableCell = (cell: TableCellNode): void => {
  const children = cell.getChildren();
  if (!children.some(isWrappableInline)) {
    return;
  }

  let currentParagraph: ParagraphNode | null = null;
  for (const child of children) {
    if (isWrappableInline(child)) {
      if (!currentParagraph) {
        currentParagraph = $createParagraphNode();
        child.insertBefore(currentParagraph);
      }
      // Moves the inline child out of the cell and into the paragraph.
      currentParagraph.append(child);
    } else {
      // Reset on any block/decorator child so the original ordering is kept.
      currentParagraph = null;
    }
  }
};

export const NormalizeTableCellsPlugin = (): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // The editor may be configured without table nodes (e.g. plain-text
    // surfaces or some tests); registering a transform for an unregistered
    // node throws, so bail out early in that case.
    if (!editor.hasNodes([TableCellNode, EnhancedTableCellNode])) {
      return;
    }

    const transform = (node: TableCellNode): void => {
      if ($isTableCellNode(node)) {
        normalizeTableCell(node);
      }
    };

    // EnhancedTableCellNode has its own node type, so it needs its own
    // transform registration in addition to the base TableCellNode.
    const removeBase = editor.registerNodeTransform(TableCellNode, transform);
    const removeEnhanced = editor.registerNodeTransform(EnhancedTableCellNode, transform);

    return () => {
      removeBase();
      removeEnhanced();
    };
  }, [editor]);

  return null;
};
